import { useMsal } from '@azure/msal-react'
import {
  Box,
  Center,
  Group,
  Pagination,
  SegmentedControl,
  SimpleGrid,
  Stack,
  Text,
} from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import { notifications } from '@mantine/notifications'
import { useMap } from '@vis.gl/react-google-maps'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import BusinessNameFilter from '../components/home/BusinessNameFilter'
import FilterSection from '../components/home/FilterSection'
import LocationsMap from '../components/home/LocationsMap'
import { LocationCardSkeleton } from '../components/home/LocationsSkeleton'
import { AllLocationsCtaBanner } from '../components/location/AllLocationsCtaBanner'
import LocationCard from '../components/location/LocationCard'
import { loginRequest } from '../authConfig'
import { useSearch } from '../contexts/CityFilterContext'
import { useSessionInfo } from '../contexts/SessionContext'
import { CityDto, fetchLocations } from '../data/location.service'
import { Location } from '../models/Location'
import { COLORS } from '../utils/constants'
import { FilterState } from '../utils/filterUtils'

export const Home = () => {
  const {
    selectedCityFilter,
    searchTriggered,
    setSearchTriggered,
    setSelectedCityFilter,
  } = useSearch()

  const [locations, setLocations] = useState<Location[]>([])
  const [totalResults, setTotalResults] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const isMobile = useMediaQuery('(max-width: 768px)')
  const [viewMode, setViewMode] = useState<'list' | 'map'>('list')
  const [businessNameSearch, setBusinessNameSearch] = useState('')
  const [mapCenter, setMapCenter] = useState<{
    lat: number
    lng: number
  } | null>(null)
  const [mapZoom, setMapZoom] = useState(10)
  const [mapBounds, setMapBounds] = useState<{
    north: number
    south: number
    east: number
    west: number
  } | null>(null)
  const map = useMap()
  const [citiesData, setCitiesData] = useState<CityDto[]>([])
  const { session } = useSessionInfo()
  const { instance } = useMsal()
  const navigate = useNavigate()

  const handleBookAllLocations = () => {
    if (!session) {
      instance.loginRedirect(loginRequest)
      return
    }
    navigate('/configure-ad/all-locations')
  }

  // Function to recenter map on a city
  const recenterMapOnCity = useCallback(
    (cityFilter: string | null) => {
      if (!cityFilter) return

      try {
        const [cityName, state] = cityFilter.split(', ').map((s) => s.trim())

        if (cityName && state) {
          const cityData = citiesData.find(
            (city) => city.name === cityName && city.state === state,
          )

          if (cityData) {
            const cityCenter = {
              lat: cityData.latitude,
              lng: cityData.longitude,
            }

            setMapCenter(cityCenter)
            setMapZoom(12)

            // Wait for map to be ready before setting center
            const waitForMap = () => {
              if (map) {
                map.setCenter(cityCenter)
                map.setZoom(12)
              } else {
                setTimeout(waitForMap, 100)
              }
            }
            waitForMap()
          }
        }
      } catch (error) {
        console.error('Error centering map on city:', error)
      }
    },
    [citiesData, map, viewMode],
  )

  // Initialize filters
  const [filters, setFilters] = useState<FilterState>({
    category: null,
    autoApprovalOnly: false,
    priceRange: [null, null],
    businessName: '',
    screenSizeRange: [null, null],
    footTraffic: [],
    currentPage: 1,
  })

  const resultsPerPage = 20
  const skeletonArray = new Array(8).fill(0)
  const totalPages = Math.ceil(totalResults / resultsPerPage)

  const fetchData = async (
    filterValues: FilterState,
    cityFilter?: string | null,
    boundingBox?: {
      north: number
      south: number
      east: number
      west: number
    } | null,
  ) => {
    try {
      setIsLoading(true)
      const skip = (filterValues.currentPage - 1) * resultsPerPage
      // Prepare price and screen size params for backend
      const priceRange = filterValues.priceRange
      const screenSizeRange = filterValues.screenSizeRange
      const city = cityFilter !== undefined ? cityFilter : selectedCityFilter

      const resp = await fetchLocations(
        filterValues.category != null ? [filterValues.category] : null,
        city,
        boundingBox,
        filterValues.autoApprovalOnly,
        priceRange[0] != null || priceRange[1] != null
          ? (priceRange as [number, number])
          : undefined,
        filterValues.businessName,
        screenSizeRange[0] != null || screenSizeRange[1] != null
          ? (screenSizeRange as [number, number])
          : undefined,
        filterValues.footTraffic,
        skip,
        resultsPerPage,
      )

      setLocations(resp.locations)
      setTotalResults(resp.totalCount)
    } catch (ex) {
      notifications.show({
        title: 'An unknown error occurred',
        message:
          'Unable to get locations. Refresh the page. If you are unable to resolve the issue, please contact us for help',
        autoClose: false,
      })
    } finally {
      setIsLoading(false)
      setSearchTriggered(false)
    }
  }

  // Handle view mode changes
  const handleViewModeChange = (newViewMode: 'list' | 'map') => {
    setViewMode(newViewMode)

    // If switching from map to list view, refetch without coordinate filtering
    if (viewMode === 'map' && newViewMode === 'list') {
      // Clear map bounds to remove coordinate filtering
      setMapBounds(null)
      // Refetch data without bounding box
      fetchData(filters, selectedCityFilter, null)
    }

    // If switching to map view and there's a city selected, center the map on that city
    if (viewMode === 'list' && newViewMode === 'map' && selectedCityFilter) {
      recenterMapOnCity(selectedCityFilter)
    }
  }

  // Handle city filter changes
  const handleCityFilterChange = async (newCity: string | null) => {
    setSelectedCityFilter(newCity)

    if (newCity && viewMode === 'map') {
      recenterMapOnCity(newCity)
      // Don't fetch data here - let the map bounds change trigger the search
      return
    }

    // For list view or when no city is selected, fetch data normally
    fetchData(filters, newCity)
  }

  // Handle map view changes
  const handleMapViewChange = (
    center: { lat: number; lng: number },
    zoom: number,
    bounds?: {
      north: number
      south: number
      east: number
      west: number
    } | null,
  ) => {
    setMapCenter(center)
    setMapZoom(zoom)
    if (bounds) {
      setMapBounds(bounds)
    }

    // Only search if zoom level is reasonable (not too zoomed out)
    // Minimum zoom level of 8 prevents searching the entire world
    if (zoom >= 8 && bounds) {
      // In map view, don't use city filter - rely only on map bounds
      fetchData(filters, null, bounds)
    }
  }

  useEffect(() => {
    if (searchTriggered && !isLoading) {
      fetchData(filters, selectedCityFilter)

      // If in map mode and city is selected, recenter the map
      if (viewMode === 'map' && selectedCityFilter) {
        recenterMapOnCity(selectedCityFilter)
      }
    }
  }, [
    searchTriggered,
    isLoading,
    filters,
    viewMode,
    selectedCityFilter,
    citiesData,
    map,
  ])

  useEffect(() => {
    fetchData(filters, selectedCityFilter)
  }, [])

  const handleFiltersChange = (newFilters: FilterState) => {
    setFilters(newFilters)
  }

  const handlePageChange = (page: number) => {
    const newFilters = { ...filters, currentPage: page }
    setFilters(newFilters)
    fetchData(newFilters, selectedCityFilter)
  }

  // Callback to receive cities data from FilterSection
  const handleCitiesLoaded = (cities: CityDto[]) => {
    setCitiesData(cities)
  }

  // Function to trigger search with specific filter values
  const triggerSearchWithFilters = useCallback(
    (newFilters: FilterState, cityFilter?: string | null) => {
      // In map view, don't use city filter - rely only on map bounds
      const cityToUse =
        viewMode === 'map'
          ? null
          : cityFilter !== undefined
          ? cityFilter
          : selectedCityFilter

      fetchData(newFilters, cityToUse, viewMode === 'map' ? mapBounds : null)
    },
    [selectedCityFilter, viewMode, mapBounds],
  )

  return (
    <Stack h='100%' gap={0}>
      {viewMode === 'list' ? (
        <>
          <Box px={30} pt={24} pb={0}>
            <AllLocationsCtaBanner
              onBookAllLocations={handleBookAllLocations}
            />
          </Box>

          <Group
            justify={isMobile ? 'flex-end' : 'space-between'}
            align='center'
            pt={0}
            pb={4}
            px={30}
          >
            <Group wrap='nowrap' w={isMobile ? '100%' : 'auto'} h={50}>
              {!isMobile && (
                <BusinessNameFilter
                  businessNameSearch={businessNameSearch}
                  setBusinessNameSearch={setBusinessNameSearch}
                  triggerSearchWithFilters={triggerSearchWithFilters}
                  currentFilters={filters}
                  onFiltersChange={handleFiltersChange}
                />
              )}
              <FilterSection
                filters={filters}
                onFiltersChange={handleFiltersChange}
                triggerSearchWithFilters={triggerSearchWithFilters}
                businessNameSearch={businessNameSearch}
                setBusinessNameSearch={setBusinessNameSearch}
                onCityFilterChange={handleCityFilterChange}
                onCitiesLoaded={handleCitiesLoaded}
              />
            </Group>
            <SegmentedControl
              value={viewMode}
              onChange={(value) =>
                handleViewModeChange(value as 'list' | 'map')
              }
              data={[
                { label: 'List View', value: 'list' },
                { label: 'Map View', value: 'map' },
              ]}
              radius='xl'
              color={COLORS.LightGreen}
            />
          </Group>

          {locations.length === 0 && !isLoading ? (
            <Center h='100%'>No Locations Found</Center>
          ) : (
            <>
              <SimpleGrid
                cols={{ base: 1, sm: 2, md: 3, lg: 4, xl: 5 }}
                spacing={50}
                px={30}
                pt={8}
                pb={30}
                style={{ alignItems: 'flex-start' }}
              >
                {isLoading
                  ? skeletonArray.map((_, index) => (
                      <LocationCardSkeleton key={index} />
                    ))
                  : locations
                      .filter((l) => l.devices != null && l.devices.length > 0)
                      .map((l) => <LocationCard location={l} key={l.id} />)}
              </SimpleGrid>

              {/* Pagination at bottom */}
              {totalResults > 0 && (
                <Group
                  justify='space-between'
                  align='center'
                  p='md'
                  style={{ borderTop: '1px solid lightgray' }}
                >
                  <Text size='sm' c='dimmed'>
                    Showing {(filters.currentPage - 1) * resultsPerPage + 1} to{' '}
                    {Math.min(
                      filters.currentPage * resultsPerPage,
                      totalResults,
                    )}{' '}
                    of {totalResults} results
                  </Text>

                  <Pagination
                    total={totalPages}
                    value={filters.currentPage}
                    onChange={handlePageChange}
                    size={isMobile ? 'sm' : 'md'}
                  />
                </Group>
              )}
            </>
          )}
        </>
      ) : (
        <Box style={{ position: 'relative', height: '100vh', width: '100%' }}>
          {/* Map fills the entire container */}
          <Box style={{ height: '100%', width: '100%' }}>
            <LocationsMap
              locations={locations}
              center={mapCenter}
              zoom={mapZoom}
              onViewChange={handleMapViewChange}
            />
          </Box>

          {/* No Results overlay for map view */}
          {locations.length === 0 && !isLoading && (
            <Box
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                zIndex: 200,
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(10px)',
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                padding: '16px 24px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              }}
            >
              <Text size='lg' fw={500} ta='center'>
                No Results Found
              </Text>
              <Text size='sm' c='dimmed' ta='center' mt={4}>
                Try adjusting your filters or zooming out
              </Text>
            </Box>
          )}

          {/* Overlay filters on top of map */}
          <Box
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              zIndex: 100,
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
              borderBottom: '1px solid #e0e0e0',
              padding: '12px 30px',
            }}
          >
            <Group justify='space-between' align='center'>
              <Group wrap='nowrap' w={isMobile ? '100%' : 'auto'} h={50}>
                {!isMobile && (
                  <BusinessNameFilter
                    businessNameSearch={businessNameSearch}
                    setBusinessNameSearch={setBusinessNameSearch}
                    triggerSearchWithFilters={triggerSearchWithFilters}
                    currentFilters={filters}
                    onFiltersChange={handleFiltersChange}
                  />
                )}
                <FilterSection
                  filters={filters}
                  onFiltersChange={handleFiltersChange}
                  triggerSearchWithFilters={triggerSearchWithFilters}
                  businessNameSearch={businessNameSearch}
                  setBusinessNameSearch={setBusinessNameSearch}
                  onCityFilterChange={handleCityFilterChange}
                  onCitiesLoaded={handleCitiesLoaded}
                />
              </Group>
              <SegmentedControl
                h={'100%'}
                value={viewMode}
                onChange={(value) =>
                  handleViewModeChange(value as 'list' | 'map')
                }
                data={[
                  { label: 'List View', value: 'list' },
                  { label: 'Map View', value: 'map' },
                ]}
                radius='xl'
                color={COLORS.LightGreen}
                visibleFrom={'sm'}
              />
            </Group>
          </Box>

          {/* Mobile segmented control at top right */}
          {isMobile && viewMode === 'map' && (
            <Box
              style={{
                position: 'absolute',
                top: 90,
                right: 20,
                zIndex: 100,
              }}
            >
              <SegmentedControl
                value={viewMode}
                onChange={(value) =>
                  handleViewModeChange(value as 'list' | 'map')
                }
                data={[
                  { label: 'List View', value: 'list' },
                  { label: 'Map View', value: 'map' },
                ]}
                size='sm'
                radius='xl'
                color={COLORS.LightGreen}
                style={{
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                  background: 'rgba(255, 255, 255, 0.95)',
                  backdropFilter: 'blur(10px)',
                }}
              />
            </Box>
          )}
        </Box>
      )}

    </Stack>
  )
}
